import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import Dayjs from 'dayjs'
import React from 'react'

import { fetchCentreDashboard, transferBudget } from 'api/procurement'
import constants from 'app/constants'
import { eventAlert, notify } from 'app/helpers'

import { PROCUREMENT_CATEGORIES } from '../constants'

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    flexWrap: 'wrap'
  },
  field: { flex: '1 1 180px', minWidth: 160 },
  preview: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    background: theme.palette.grey[100],
    borderRadius: 4
  },
  warn: { color: theme.palette.warning.main, marginTop: theme.spacing(1) },
  err: { color: theme.palette.error.main, marginTop: theme.spacing(1) }
}))

const labelFor = (key) =>
  PROCUREMENT_CATEGORIES.find((c) => c.key === key)?.label || key

const rupee = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`

const TransferDialog = ({ open, onClose, centres, month, monthDate, onDone }) => {
  const classes = useStyles()

  const [centre, setCentre] = React.useState('')
  const [fromCat, setFromCat] = React.useState('')
  const [toCat, setToCat] = React.useState('')
  const [amount, setAmount] = React.useState('')
  // per-category { base (budget), utilised (used) } for the selected centre
  const [usage, setUsage] = React.useState(null)
  const [loadingUsage, setLoadingUsage] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    if (!open) {
      setCentre('')
      setFromCat('')
      setToCat('')
      setAmount('')
      setUsage(null)
    }
  }, [open])

  React.useEffect(() => {
    let active = true
    if (!centre) {
      setUsage(null)
      return undefined
    }
    setLoadingUsage(true)
    fetchCentreDashboard({ centre, month: monthDate })
      .then((data) => {
        if (!active) return
        const map = {}
        ;(data?.categories || []).forEach((c) => {
          map[c.key] = { base: Number(c.base || 0), utilised: Number(c.utilised || 0) }
        })
        setUsage(map)
      })
      .catch(() => active && setUsage({}))
      .finally(() => active && setLoadingUsage(false))
    return () => {
      active = false
    }
  }, [centre, monthDate])

  const amt = Number(amount)
  const fromInfo = (usage && usage[fromCat]) || { base: 0, utilised: 0 }
  const toInfo = (usage && usage[toCat]) || { base: 0, utilised: 0 }

  const sameCat = fromCat && toCat && fromCat === toCat
  const overSource = fromCat && amt > 0 && amt > fromInfo.base
  // Non-blocking: leaves the source category below what it has already spent.
  const belowSpent =
    fromCat && amt > 0 && !overSource && fromInfo.base - amt < fromInfo.utilised

  const canSubmit =
    centre &&
    fromCat &&
    toCat &&
    !sameCat &&
    amt > 0 &&
    !overSource &&
    !loadingUsage &&
    !submitting

  const onSubmit = async () => {
    setSubmitting(true)
    try {
      await transferBudget({
        centre,
        month: monthDate,
        from_category: fromCat,
        to_category: toCat,
        amount: amt
      })
      notify(
        `Moved ${rupee(amt)} from ${labelFor(fromCat)} to ${labelFor(toCat)}`,
        '',
        'success'
      )
      onDone()
      onClose()
    } catch (error) {
      eventAlert(
        error?.message || 'Transfer failed',
        constants.ERROR,
        'Budget transfer'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Transfer budget — {Dayjs(monthDate).format('MMMM YYYY')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary">
          Moves budget between two categories of the same centre. The centre&apos;s
          total for the month stays the same.
        </Typography>

        <div className={classes.row}>
          <TextField
            select
            className={classes.field}
            label="Centre"
            size="small"
            variant="outlined"
            value={centre}
            onChange={(e) => setCentre(e.target.value)}>
            {centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <div className={classes.row}>
          <TextField
            select
            className={classes.field}
            label="From"
            size="small"
            variant="outlined"
            value={fromCat}
            disabled={!centre}
            onChange={(e) => setFromCat(e.target.value)}>
            {PROCUREMENT_CATEGORIES.map((c) => (
              <MenuItem key={c.key} value={c.key} disabled={c.key === toCat}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            className={classes.field}
            label="To"
            size="small"
            variant="outlined"
            value={toCat}
            disabled={!centre}
            onChange={(e) => setToCat(e.target.value)}>
            {PROCUREMENT_CATEGORIES.map((c) => (
              <MenuItem key={c.key} value={c.key} disabled={c.key === fromCat}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            className={classes.field}
            label="Amount (₹)"
            size="small"
            variant="outlined"
            value={amount}
            disabled={!centre}
            onChange={(e) => {
              const v = e.target.value
              if (v === '' || !Number.isNaN(Number(v))) setAmount(v)
            }}
          />
        </div>

        {loadingUsage && (
          <Box display="flex" alignItems="center" mt={2}>
            <CircularProgress size={18} />
            <Box ml={1}>
              <Typography variant="body2">Loading current budgets…</Typography>
            </Box>
          </Box>
        )}

        {!loadingUsage && fromCat && toCat && !sameCat && amt > 0 && (
          <div className={classes.preview}>
            <Typography variant="body2">
              {labelFor(fromCat)}: {rupee(fromInfo.base)} →{' '}
              <b>{rupee(fromInfo.base - amt)}</b>
            </Typography>
            <Typography variant="body2">
              {labelFor(toCat)}: {rupee(toInfo.base)} →{' '}
              <b>{rupee(toInfo.base + amt)}</b>
            </Typography>
          </div>
        )}

        {overSource && (
          <Typography variant="body2" className={classes.err}>
            Only {rupee(fromInfo.base)} is budgeted for {labelFor(fromCat)} this
            month — you can&apos;t transfer more than that.
          </Typography>
        )}

        {belowSpent && (
          <Typography variant="body2" className={classes.warn}>
            This leaves {labelFor(fromCat)} at {rupee(fromInfo.base - amt)}, below
            the {rupee(fromInfo.utilised)} already spent there — allowed, but it
            will show as over budget.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          disabled={!canSubmit}>
          {submitting ? 'Transferring…' : 'Transfer'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TransferDialog
